import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'

export const LUXURY_PLUS = {
  PROGRAM_NAME: 'LuxPlus+',
  ANALYTICS_CATEGORY: 'LuxPlus',
  /**
   * The number of days before a user can no longer cancel their membership
   */
  REFUND_ELIGIBILITY_PERIOD_IN_DAYS: 14,
  RENEWAL_GRACE_PERIOD_IN_DAYS: 14,
  FREE_PREVIEW_DURATION: 28,
  DISMISS_FREE_PREVIEW_STORAGE_KEY: 'lux-plus-preview-dismiss',
  FREE_PREVIEW_AUTO_ENROLL_QUERY_PARAM: 'lux-plus-free-preview-auto-enroll',
  FREE_PREVIEW_QUERY_PARAM: 'lux-plus-free-preview',
  HIGH_PERCENTAGE_THRESHOLD: 70,
  LOW_PERCENTAGE_THRESHOLD: 50,
  INSURANCE_DISCOUNT_PERCENTAGE: 40,
  MIN_INSURANCE_DISCOUNT_THRESHOLD: 30,
  MAX_INSURANCE_DISCOUNT_THRESHOLD: 300,
  /**
   * Minimum savings amount (in display currency) required to show the
   * LuxPlus savings banner to non-members
   */
  NON_MEMBER_SAVINGS_DISPLAY_THRESHOLD: 50,
  /**
   * Minimum savings amount (in display currency) required to show the
   * personalised savings stat card on the landing page.
   */
  LANDING_PAGE_PERSONALISED_SAVINGS_THRESHOLD: 100,
} as const

export const LUXURY_PLUS_PATHS = {
  LANDING_PAGE: `lux-plus?${LUXURY_PLUS.FREE_PREVIEW_QUERY_PARAM}=true`,
  DEFAULT_LANDING_PAGE: 'lux-plus',
  ACCOUNT_MANAGEMENT_PAGE: 'account/lux-plus',
  TERMS_AND_CONDITIONS_PAGE: 'lux-plus-terms-and-conditions',
} as const

/**
 * Per-region minimum past savings threshold required to show the
 * accumulative savings banner on the checkout LuxPlus+ upsell step.
 * Roughly 40% of each region's subscription price.
 */
export const CHECKOUT_ACCUMULATIVE_SAVINGS_THRESHOLDS: Record<string, number> = {
  AE: 240,
  AU: 100,
  CA: 92,
  CN: 475,
  DE: 60,
  ES: 60,
  FR: 60,
  GB: 52,
  HK: 355,
  ID: 996_000,
  IE: 60,
  IL: 240,
  IT: 60,
  JP: 10_200,
  KR: 87_600,
  MO: 520,
  MY: 240,
  NL: 60,
  NZ: 100,
  PH: 3_720,
  QA: 240,
  SA: 240,
  SG: 80,
  TH: 2_000,
  TW: 1_555,
  US: 70,
  VN: 1_596_000,
  ZA: 1_200,
}

/**
 * Per-region net saving threshold below which the reconsider banner
 * switches from showing net savings to showing member price hotel savings.
 * Roughly 4% of each region's subscription price.
 */
export const RECONSIDER_LOW_NET_SAVING_THRESHOLDS: Record<string, number> = {
  AE: 25,
  AU: 10,
  CA: 10,
  CN: 50,
  DE: 6,
  ES: 6,
  FR: 6,
  GB: 5,
  HK: 35,
  ID: 100_000,
  IE: 6,
  IL: 25,
  IT: 6,
  JP: 1_000,
  KR: 9_000,
  MO: 50,
  MY: 25,
  NL: 6,
  NZ: 10,
  PH: 370,
  QA: 25,
  SA: 25,
  SG: 8,
  TH: 200,
  TW: 155,
  US: 7,
  VN: 160_000,
  ZA: 120,
}

export const LUX_PLUS_PROMOTIONAL_OFFER_PARAM = 'luxplusExclusiveOffer'

export const LUX_PLUS_GIFT_CARD_CAMPAIGN = {
  PROMO_CODE: 'LPGIFT',
  START_DATE: new Date('2026-02-13T00:00:00+11:00'),
  END_DATE: new Date('2026-02-27T23:59:59+11:00'),
  MAX_PURCHASE_AMOUNT: 5000,
  /** Override/hide lab (incl. Maldives, Lunar New Year, etc.) campaign banner until date below */
  SHOULD_OVERRIDE_LAB_CAMPAIGN: dateIsBeforeOrSame(new Date(), new Date('2026-02-16T23:59:59+11:00')),
} as const

export const LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN = {
  ELIGIBLE_REGION: 'au',
  START_DATE: new Date('2026-03-18T00:00:00+11:00'),
  END_DATE: new Date('2026-04-04T23:59:59+11:00'),
  VOUCHER_TITLE: 'LuxPlus+ Flights Voucher',
  DEFAULT_VOUCHER_COUNT: 2,
  /** Override/hide lab campaign banner until date below */
  SHOULD_OVERRIDE_LAB_CAMPAIGN: dateIsBeforeOrSame(new Date(), new Date('2026-04-04T23:59:59+11:00')),
} as const
